import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, intlLocale } from "@/lib/api";
import { useT } from "@/i18n";
import { Shell } from "@/components/Shell";

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const sameDay = (a, b) => a.toDateString() === new Date(b).toDateString();

export default function AdminCalendar() {
  const [items, setItems] = useState([]);
  const [visits, setVisits] = useState([]);
  const [week, setWeek] = useState(startOfWeek(new Date()));
  const { t } = useT();
  const loc = intlLocale();
  useEffect(() => { api.get("/admin/calendar").then((r) => setItems(r.data)); api.get("/admin/visits-365").then((r) => setVisits(r.data)).catch(() => {}); }, []);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(week, i)), [week]);
  const entries = useMemo(() => [...items.flatMap((r) => [
    r.inspection_date && { kind: t("admin.inspection"), date: r.inspection_date, r, cls: "border-indigo-300 bg-indigo-50" },
    r.scheduled_date && { kind: r.work_status === "completato" ? t("admin.completed") : t("admin.intervention"), date: r.scheduled_date, r, cls: r.work_status === "in_corso" ? "border-emerald-400 bg-emerald-50" : "border-terracotta/40 bg-terracotta-light/50" },
  ].filter(Boolean)), ...visits.map((v) => ({ kind: t("admin.visit365"), date: `${v.deadline.due_date}T09:00:00`, cls: "border-forest bg-forest/10",
    r: { id: v.id, number: `365-${v.deadline.id.slice(0, 4)}`, service_name: `Ci penso io 365 · ${v.home_name}`, customer_name: v.customer_name, city: v.city, assigned_name: t("admin.scheduledVisit"), is365: true } }))], [items, visits, t]);

  return (
    <Shell wide>
      <div className="flex flex-wrap items-end justify-between gap-4 rise">
        <div><p className="eyebrow">{t("admin.backoffice")}</p><h1 className="mt-2 font-serif text-4xl sm:text-5xl font-bold">{t("admin.calendar")}</h1></div>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev" className="btn-secondary !p-2.5" onClick={() => setWeek(addDays(week, -7))}><ChevronLeft size={16} /></button>
          <button data-testid="calendar-today" className="btn-secondary !py-2" onClick={() => setWeek(startOfWeek(new Date()))}>{t("admin.today")}</button>
          <button data-testid="calendar-next" className="btn-secondary !p-2.5" onClick={() => setWeek(addDays(week, 7))}><ChevronRight size={16} /></button>
        </div>
      </div>
      <p className="mt-2 font-mono text-sm text-ink/50">{days[0].toLocaleDateString(loc, { day: "2-digit", month: "long" })} – {days[6].toLocaleDateString(loc, { day: "2-digit", month: "long", year: "numeric" })}</p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="calendar-grid">
        {days.map((d) => {
          const today = sameDay(d, new Date());
          const list = entries.filter((e) => sameDay(d, e.date)).sort((a, b) => a.date.localeCompare(b.date));
          return (
            <div key={d} className={`card-craft p-3 min-h-[180px] ${today ? "!border-terracotta" : ""}`}>
              <div className="flex items-baseline justify-between"><span className="text-xs uppercase tracking-wider text-ink/50">{d.toLocaleDateString(loc, { weekday: "short" })}</span><span className={`font-mono text-lg ${today ? "text-terracotta font-bold" : ""}`}>{d.getDate()}</span></div>
              <div className="mt-2 space-y-2">
                {list.map((e, i) => (
                  <Link key={`${e.r.id}-${e.kind}-${e.date}`} to={e.r.is365 ? `/admin/case/${e.r.id}` : `/admin/richieste/${e.r.id}`} className={`block rounded-lg border-l-4 p-2 text-xs transition-transform hover:-translate-y-0.5 ${e.cls}`} data-testid={`calendar-entry-${e.r.number}`}>
                    <div className="font-mono">{new Date(e.date).toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" })} · {e.kind}</div>
                    <div className="font-semibold mt-0.5">{e.r.service_name}</div>
                    <div className="text-ink/60">{e.r.customer_name} · {e.r.city}</div>
                    {e.kind !== t("admin.inspection") && <div className="text-ink/60">{e.r.assigned_name || t("admin.unassigned")}</div>}
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Shell>
  );
}
